// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Clatter — the rules, as pure functions over a plain state. No DOM, no clock, no network, which is
// what lets the bot, the balance sim, the mechanism audit and the netcode all drive the SAME code.
//
// THE ONE STRUCTURAL DECISION, and everything follows from it: an action packs into a single int
// whose HIGH bits are (turn, phase, seat). Sorting the log ascending is therefore the canonical
// order, and the whole state is a pure function `replay(mode, seed, n, log)`. Two consequences:
//
//   1. The passive picks inside one turn touch only their own player's sheet, so they genuinely
//      COMMUTE. Every player can act at once instead of waiting their turn around the table — the
//      single worst failure mode the design panel found in every candidate ruleset (a blocking
//      N-1 prompt chain per turn, which is phone-hostile and P2P-hostile).
//   2. Merging two peers' logs is a sorted union, and the state is recomputed rather than patched.
//      There is no incremental-apply path that can diverge, because there is no incremental apply.
//
// Every die comes from the round seed, so no roll is ever sent. A received action that is not in
// the local legal set means the two peers are running different rules — a stale cached build — and
// it is reported as exactly that rather than silently applied.

import { makeRng } from '@ben-gy/game-engine/rng';
import { modeOf, type Mode } from './modes';

/** Bump when the wire meaning of an action, the sheet, or the scoring changes. */
export const RULES_REV = 1;

// ── tracks ──────────────────────────────────────────────────────────────────────
export const T_LADDER = 0;
export const T_TALLY = 1;
export const T_RUN = 2;
export const T_WEIGHTS = 3;
export const TRACKS = [T_LADDER, T_TALLY, T_RUN, T_WEIGHTS] as const;
/** Sentinel track for "I took a die that has no home" / "I take nothing". */
export const T_NONE = 7;

export const TRACK_NAME = ['Ladder', 'Tally', 'Run', 'Weights'];
export const TRACK_GLYPH = ['▲', '●', '◆', '■'];
export const TRACK_KEY = ['ladder', 'tally', 'run', 'weights'] as const;

export const LADDER_CELLS = 5;
export const TALLY_COLS = 6;
export const TALLY_DEPTH = 2;
export const RUN_CELLS = 7;
export const WEIGHT_PAIRS = 4;
export const TOTAL_CELLS = LADDER_CELLS + TALLY_COLS * TALLY_DEPTH + RUN_CELLS + WEIGHT_PAIRS * 2;

/** The Run's printed floors: a die may enter cell k only if its face is ≥ RUN_FLOOR[k]. */
export const RUN_FLOOR = [1, 2, 3, 3, 4, 5, 6];

/** Cumulative score for n filled cells. Back-loaded, so depth is the reward. */
export const LADDER_SCORE = [0, 2, 5, 9, 14, 20];
export const RUN_SCORE = [0, 1, 3, 6, 10, 15, 21, 28];
export const TALLY_COL_SCORE = [0, 2, 6];
export const TALLY_ALL_BONUS = 8;
export const WEIGHT_PAIR_SCORE = 6;
export const WEIGHT_ALL_BONUS = 10;
/**
 * An unspent shim is worth this at the end — the same as a shim gained at the cap.
 *
 * MEASURED, not chosen. At 2 points a shim was worth more than most placements, so the bots simply
 * declined the spill all game: at four players they finished with SEVEN of thirty-two cells filled
 * and a score made almost entirely of banked shims. A roll-and-write whose optimal line is "write
 * nothing" is not a roll-and-write. See tests/balance.test.ts, which pins the fill rate.
 */
export const SHIM_POINTS = 1;
export const SHIM_CAP = 3;

// ── printed bonuses ─────────────────────────────────────────────────────────────
export const B_NONE = 0;
export const B_FREE_LADDER = 1;
export const B_FREE_TALLY = 2;
export const B_FREE_RUN = 3;
export const B_FREE_WEIGHT = 4;
export const B_SHIM = 5;
/** +N points. Encoded as B_PTS + N so one small int carries the whole table. */
export const B_PTS = 100;

/** Ladder cell index → bonus. */
export const LADDER_BONUS = [B_NONE, B_FREE_TALLY, B_NONE, B_SHIM, B_NONE];
/** Tally column index → bonus, fired when that column reaches full depth. */
export const TALLY_BONUS = [B_PTS + 4, B_NONE, B_FREE_RUN, B_NONE, B_SHIM, B_NONE];
/** Run cell index → bonus. */
export const RUN_BONUS = [B_NONE, B_NONE, B_FREE_WEIGHT, B_NONE, B_FREE_LADDER, B_NONE, B_PTS + 6];
/** Weight pair index → bonus, fired when the pair balances. */
export const WEIGHT_BONUS = [B_NONE, B_SHIM, B_FREE_RUN, B_NONE];

// ── the sheet ───────────────────────────────────────────────────────────────────
export interface Sheet {
  /** Faces written, left to right. -1 = empty. Strictly increasing. */
  ladder: number[];
  /** Depth 0..TALLY_DEPTH per column, indexed by face-1. */
  tally: number[];
  /** How many Run cells are struck. */
  run: number;
  /**
   * Per pair: 0 = empty, 1..6 = open, holding that face and printing 7−face in the empty pan,
   * 10 = balanced. A pair is opened by any face and balanced only by its exact complement.
   */
  weights: number[];
  shims: number;
  /** Points from printed +N cells and from shims gained at the cap. */
  pts: number;
  /**
   * Tribute mode only: a point for every rival who took a die out of this player's spill.
   *
   * This replaced a rule that paid you for the dice that did NOT spill, which the balance sim
   * killed outright: it paid you to switch the game's signature mechanic off, and the bots duly
   * starved the table on 63-91% of turns. Paying for UPTAKE instead of for withholding points the
   * incentive at the mechanic rather than away from it.
   */
  tips: number;
  /** Dice actually written onto the sheet. */
  written: number;
  /** Turns this player took nothing (declined, dead die, or an empty spill). */
  passed: number;
  /** Free marks gained from the printed bonuses. */
  chain: number;
  /** Free marks that had nowhere to go and became shims. */
  fizzled: number;
}

export function createSheet(): Sheet {
  return {
    ladder: new Array<number>(LADDER_CELLS).fill(-1),
    tally: new Array<number>(TALLY_COLS).fill(0),
    run: 0,
    weights: new Array<number>(WEIGHT_PAIRS).fill(0),
    shims: 0,
    pts: 0,
    tips: 0,
    written: 0,
    passed: 0,
    chain: 0,
    fizzled: 0,
  };
}

export function cloneSheet(s: Sheet): Sheet {
  return {
    ladder: s.ladder.slice(),
    tally: s.tally.slice(),
    run: s.run,
    weights: s.weights.slice(),
    shims: s.shims,
    pts: s.pts,
    tips: s.tips,
    written: s.written,
    passed: s.passed,
    chain: s.chain,
    fizzled: s.fizzled,
  };
}

export const ladderFilled = (s: Sheet): number => s.ladder.filter((v) => v >= 0).length;
export const weightsBalanced = (s: Sheet): number => s.weights.filter((w) => w === 10).length;
export const cellsFilled = (s: Sheet): number =>
  ladderFilled(s) + s.tally.reduce((a, b) => a + b, 0) + s.run + s.weights.reduce((a, w) => a + (w === 10 ? 2 : w > 0 ? 1 : 0), 0);

// ── scoring ─────────────────────────────────────────────────────────────────────
export interface Breakdown {
  ladder: number;
  tally: number;
  run: number;
  weights: number;
  pts: number;
  tips: number;
  shims: number;
  total: number;
}

export function score(s: Sheet, mode: Mode): Breakdown {
  const lc = ladderFilled(s);
  const ladderRaw = LADDER_SCORE[lc];

  let tallyRaw = 0;
  let allCols = true;
  for (const d of s.tally) {
    tallyRaw += TALLY_COL_SCORE[d];
    if (d === 0) allCols = false;
  }
  if (allCols) tallyRaw += TALLY_ALL_BONUS;

  const runRaw = RUN_SCORE[s.run];

  const bal = weightsBalanced(s);
  const weightsRaw = bal * WEIGHT_PAIR_SCORE + (bal === WEIGHT_PAIRS ? WEIGHT_ALL_BONUS : 0);

  // Scramble counts only your best `scoreBest` tracks. Everything else counts all four.
  let keepSet = [ladderRaw, tallyRaw, runRaw, weightsRaw];
  if (mode.scoreBest > 0 && mode.scoreBest < 4) {
    const ranked = keepSet.map((v, i) => [v, i] as const).sort((a, b) => b[0] - a[0] || a[1] - b[1]);
    const live = new Set(ranked.slice(0, mode.scoreBest).map(([, i]) => i));
    keepSet = keepSet.map((v, i) => (live.has(i) ? v : 0));
  }
  const [ladder, tally, run, weights] = keepSet;

  const shims = s.shims * SHIM_POINTS;
  const total = ladder + tally + run + weights + s.pts + s.tips + shims;
  return { ladder, tally, run, weights, pts: s.pts, tips: s.tips, shims, total };
}

export const totalScore = (s: Sheet, mode: Mode): number => score(s, mode).total;

// ── placement legality ──────────────────────────────────────────────────────────
/**
 * Can `face` go into `track` at `slot`? Slot is the weight-pair index and is ignored elsewhere.
 * Every other track's cell is FORCED by the rules, which is why a placement is one tap.
 */
export function canPlace(s: Sheet, track: number, face: number, slot: number): boolean {
  if (face < 1 || face > 6) return false;
  switch (track) {
    case T_LADDER: {
      const k = ladderFilled(s);
      if (k >= LADDER_CELLS) return false;
      return k === 0 ? true : face > s.ladder[k - 1];
    }
    case T_TALLY:
      return s.tally[face - 1] < TALLY_DEPTH;
    case T_RUN:
      return s.run < RUN_CELLS && face >= RUN_FLOOR[s.run];
    case T_WEIGHTS: {
      if (slot < 0 || slot >= WEIGHT_PAIRS) return false;
      const w = s.weights[slot];
      if (w === 0) return true;
      if (w === 10) return false;
      return face === 7 - w;
    }
    default:
      return false;
  }
}

/** Every (track, slot) a face can legally go into. Small by construction — at most 7. */
export function placementsFor(s: Sheet, face: number): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  if (canPlace(s, T_LADDER, face, 0)) out.push([T_LADDER, 0]);
  if (canPlace(s, T_TALLY, face, 0)) out.push([T_TALLY, 0]);
  if (canPlace(s, T_RUN, face, 0)) out.push([T_RUN, 0]);
  let firstEmpty = -1;
  for (let p = 0; p < WEIGHT_PAIRS; p++) {
    const w = s.weights[p];
    if (w === 0) {
      // Opening an empty pair is the same move whichever empty pair you pick, so only the leftmost
      // is offered. Two identical actions with different slots would be two log entries for one
      // decision, and the bot would waste half its search on them.
      if (firstEmpty < 0) firstEmpty = p;
    } else if (w !== 10 && face === 7 - w) {
      out.push([T_WEIGHTS, p]);
    }
  }
  if (firstEmpty >= 0) out.push([T_WEIGHTS, firstEmpty]);
  return out;
}

export const hasAnyPlacement = (s: Sheet, face: number): boolean => placementsFor(s, face).length > 0;

// ── events, for juice and for the mechanism audit ───────────────────────────────
export type EventKind = 'write' | 'free' | 'fizzle' | 'shim' | 'pts' | 'complete' | 'dead' | 'pass' | 'tip' | 'spill';

export interface PlyEvent {
  kind: EventKind;
  seat: number;
  track?: number;
  cell?: number;
  face?: number;
  n?: number;
  /** True when this write came from a printed bonus rather than a die the player took. */
  free?: boolean;
}

// ── marking, and the cascade ────────────────────────────────────────────────────
function grantShim(s: Sheet, seat: number, ev: PlyEvent[]): void {
  if (s.shims >= SHIM_CAP) {
    s.pts += SHIM_POINTS;
    ev.push({ kind: 'pts', seat, n: SHIM_POINTS });
  } else {
    s.shims++;
    ev.push({ kind: 'shim', seat, n: s.shims });
  }
}

function fireBonus(s: Sheet, seat: number, bonus: number, queue: number[], ev: PlyEvent[]): void {
  if (bonus === B_NONE) return;
  if (bonus >= B_PTS) {
    const n = bonus - B_PTS;
    s.pts += n;
    ev.push({ kind: 'pts', seat, n });
    return;
  }
  if (bonus === B_SHIM) {
    grantShim(s, seat, ev);
    return;
  }
  queue.push(bonus);
}

/**
 * Write `face` into (track, slot) and fire whatever the cell prints. Assumes legality.
 * Returns the bonuses this write triggered, appended to `queue`.
 */
function mark(s: Sheet, seat: number, track: number, face: number, slot: number, free: boolean, queue: number[], ev: PlyEvent[]): void {
  switch (track) {
    case T_LADDER: {
      const k = ladderFilled(s);
      s.ladder[k] = face;
      ev.push({ kind: 'write', seat, track, cell: k, face, free });
      fireBonus(s, seat, LADDER_BONUS[k], queue, ev);
      if (k + 1 === LADDER_CELLS) ev.push({ kind: 'complete', seat, track });
      break;
    }
    case T_TALLY: {
      const c = face - 1;
      s.tally[c]++;
      ev.push({ kind: 'write', seat, track, cell: c, face, free });
      if (s.tally[c] === TALLY_DEPTH) fireBonus(s, seat, TALLY_BONUS[c], queue, ev);
      if (s.tally.every((d) => d === TALLY_DEPTH)) ev.push({ kind: 'complete', seat, track });
      break;
    }
    case T_RUN: {
      const k = s.run;
      s.run++;
      ev.push({ kind: 'write', seat, track, cell: k, face, free });
      fireBonus(s, seat, RUN_BONUS[k], queue, ev);
      if (s.run === RUN_CELLS) ev.push({ kind: 'complete', seat, track });
      break;
    }
    case T_WEIGHTS: {
      const w = s.weights[slot];
      if (w === 0) {
        s.weights[slot] = face;
        ev.push({ kind: 'write', seat, track, cell: slot * 2, face, free });
      } else {
        s.weights[slot] = 10;
        ev.push({ kind: 'write', seat, track, cell: slot * 2 + 1, face, free });
        fireBonus(s, seat, WEIGHT_BONUS[slot], queue, ev);
        if (weightsBalanced(s) === WEIGHT_PAIRS) ev.push({ kind: 'complete', seat, track });
      }
      break;
    }
    default:
      break;
  }
  s.written += free ? 0 : 1;
}

/**
 * Where a FREE mark lands — fully deterministic, so it costs no prompt, no extra log entry and no
 * ordering question. A free mark that has nowhere to go becomes a shim rather than being redirected
 * or queued, which is what keeps the cascade acyclic.
 */
function freeTarget(s: Sheet, bonus: number): { track: number; face: number; slot: number } | null {
  switch (bonus) {
    case B_FREE_LADDER: {
      const k = ladderFilled(s);
      if (k >= LADDER_CELLS) return null;
      const face = k === 0 ? 1 : s.ladder[k - 1] + 1;
      if (face > 6) return null;
      return { track: T_LADDER, face, slot: 0 };
    }
    case B_FREE_TALLY: {
      for (let c = 0; c < TALLY_COLS; c++) if (s.tally[c] < TALLY_DEPTH) return { track: T_TALLY, face: c + 1, slot: 0 };
      return null;
    }
    case B_FREE_RUN: {
      if (s.run >= RUN_CELLS) return null;
      return { track: T_RUN, face: RUN_FLOOR[s.run], slot: 0 };
    }
    case B_FREE_WEIGHT: {
      for (let p = 0; p < WEIGHT_PAIRS; p++) {
        const w = s.weights[p];
        if (w > 0 && w !== 10) return { track: T_WEIGHTS, face: 7 - w, slot: p };
      }
      return null;
    }
    default:
      return null;
  }
}

/**
 * Drain the cascade. TERMINATION: every queued bonus either fills an empty cell — and cells are
 * never cleared, so the count of empty cells strictly decreases — or fizzles into a shim, which
 * grants nothing. The queue is therefore bounded by TOTAL_CELLS and cannot loop. The guard below
 * is a backstop against a future bonus table that breaks the invariant, not the proof.
 */
function drain(s: Sheet, seat: number, queue: number[], ev: PlyEvent[]): void {
  let guard = TOTAL_CELLS + 4;
  while (queue.length > 0 && guard-- > 0) {
    const bonus = queue.shift() as number;
    const t = freeTarget(s, bonus);
    if (!t) {
      s.fizzled++;
      ev.push({ kind: 'fizzle', seat });
      grantShim(s, seat, ev);
      continue;
    }
    s.chain++;
    mark(s, seat, t.track, t.face, t.slot, true, queue, ev);
  }
}

/** Write a die and resolve everything it sets off. Assumes the placement is legal. */
export function place(s: Sheet, seat: number, track: number, face: number, slot: number, ev: PlyEvent[]): void {
  const queue: number[] = [];
  mark(s, seat, track, face, slot, false, queue, ev);
  drain(s, seat, queue, ev);
}

// ── the dice ────────────────────────────────────────────────────────────────────
/**
 * The pool for one active turn. Derived from (seed, turn) alone, so no roll is ever sent over the
 * wire and every peer — and every spectator, and a peer that joined late — sees the same dice.
 * Sorted descending, which is also how they are drawn: the spill is then always a suffix or a
 * prefix of what you can see.
 */
export function poolFor(seed: number, turn: number, size: number): number[] {
  const rng = makeRng(((seed >>> 0) ^ ((turn + 1) * 0x9e3779b1)) >>> 0);
  const out: number[] = [];
  for (let i = 0; i < size; i++) out.push(1 + Math.floor(rng() * 6));
  out.sort((a, b) => b - a);
  return out;
}

/**
 * What spills when the die at `idx` is taken, and what stays.
 *
 * "Every die strictly LOWER than the one you took goes to the table" — or strictly higher, in
 * Updraft, which inverts every heuristic in the game without adding a rule. What does NOT spill is
 * THE KEEP: one point each. That is the line that prices denial. Without it, taking the pool
 * minimum hands the table nothing and costs you nothing, and the design panel's judges shot exactly
 * that hole in three separate rulesets.
 */
export function splitPool(pool: number[], idx: number): { spill: number[]; stay: number } {
  const face = pool[idx];
  const spill: number[] = [];
  let stay = 0;
  for (let i = 0; i < pool.length; i++) {
    if (i === idx) continue;
    const v = pool[i];
    if (v < face) spill.push(v);
    else stay++;
  }
  spill.sort((a, b) => b - a);
  return { spill, stay };
}

// ── actions ─────────────────────────────────────────────────────────────────────
// turn:6 @25 | phase:1 @24 | seat:2 @22 | die:3 @19 | shim:2 @17 | track:3 @14 | slot:3 @11
export const PH_ACTIVE = 0;
export const PH_SPILL = 1;
/** dieIdx sentinel for "I take nothing". */
export const DIE_NONE = 7;
export const SHIM_NONE = 0;
export const SHIM_UP = 1;
export const SHIM_DOWN = 2;

export const pack = (turn: number, phase: number, seat: number, die: number, shim: number, track: number, slot: number): number =>
  ((turn << 25) | (phase << 24) | (seat << 22) | (die << 19) | (shim << 17) | (track << 14) | (slot << 11)) >>> 0;

export const aTurn = (a: number): number => (a >>> 25) & 63;
export const aPhase = (a: number): number => (a >>> 24) & 1;
export const aSeat = (a: number): number => (a >>> 22) & 3;
export const aDie = (a: number): number => (a >>> 19) & 7;
export const aShim = (a: number): number => (a >>> 17) & 3;
export const aTrack = (a: number): number => (a >>> 14) & 7;
export const aSlot = (a: number): number => (a >>> 11) & 7;
/** (turn, phase, seat) — one player's one decision. Two actions with the same key are rivals. */
export const aSlotKey = (a: number): number => a >>> 22;

export const shimDelta = (shim: number): number => (shim === SHIM_UP ? 1 : shim === SHIM_DOWN ? -1 : 0);

// ── state ───────────────────────────────────────────────────────────────────────
export interface State {
  mode: Mode;
  seed: number;
  /** Seats in this round. */
  n: number;
  log: number[];
  /** 0 … mode.turns. Equal to mode.turns when the round is over. */
  turn: number;
  phase: number;
  sheets: Sheet[];
  /** This turn's pool, sorted descending. Empty once the round is over. */
  pool: number[];
  /** Index into `pool` of the die the active player took, or -1 before they act. */
  takenIdx: number;
  /** This turn's spill, sorted descending. Only meaningful in PH_SPILL. */
  spill: number[];
  /** Which seats have already acted in PH_SPILL this turn. */
  acted: boolean[];
  over: boolean;
}

/**
 * WHOSE ACTIVE TURN IT IS — and the one place this game's fairness lives.
 *
 * Plain round-robin (`turn % n`) is not fair here, and the balance sim proved it: the LAST seat in
 * the order won 39% of four-player games against an expected 25%, under a uniform-policy control
 * with every seat running the identical bot. The mechanism is small and systematic — an active turn
 * is worth more than a spill pick, and being active late in a round means you take from a full pool
 * at the moment your own sheet is at its most constrained. It is worth about ONE point per seat
 * step, which sounds like nothing until you notice the mean winning margin is under four.
 *
 * TWO STRUCTURAL FIXES WERE TRIED FIRST AND BOTH OVERSHOT, which is worth recording because the
 * instinct to reorder the table is so strong. Reversing the order for the second half of the round
 * took seat 0 from 17% to 35% at four players — being last in the LATE half is worth more than
 * being last in the early half, so a straight swap over-pays whoever gets the late one. Rotating
 * the starting seat every round was worse still at two players (64/36), because with n=2 it makes
 * seat 0 both the very first and the very last active turn of the game.
 *
 * So the order is the plain, legible one, and the bias is paid off directly in the currency it is
 * measured in — see HEAD_START. A reorder moves an advantage somewhere you did not measure; points
 * cancel it where it actually lands.
 */
export function seatForTurn(turn: number, n: number, _turns: number): number {
  return turn % n;
}

/**
 * THE HEAD START — the seats that act earlier begin with a few points.
 *
 * SWEPT, not chosen: for every mode at every player count the whole 0..4 range was measured over
 * 600 games under a uniform-policy control, and the flattest value taken. It is indexed by PLAYER
 * COUNT as well as by mode because one number genuinely does not fit — Scramble at two players
 * wants no compensation at all (its twelve turns barely develop an order effect, so any head start
 * over-corrects), while the same mode at three or four wants two points. The worst residual
 * deviation across all nine combinations is 4.9pp, against 14pp uncompensated.
 *
 * tests/balance.test.ts pins both the table and the seat win rates it produces.
 */
export const HEAD_SPREAD: Record<string, Record<number, number>> = {
  workshop: { 2: 2, 3: 2, 4: 2 },
  scramble: { 2: 0, 3: 2, 4: 2 },
  tribute: { 2: 2, 3: 1, 4: 2 },
};

export function headStart(modeId: string, seat: number, n: number): number {
  if (n < 2) return 0;
  const byCount = Object.hasOwn(HEAD_SPREAD, modeId) ? HEAD_SPREAD[modeId] : HEAD_SPREAD.workshop;
  const total = Object.hasOwn(byCount, n) ? byCount[n] : 2;
  return Math.round((total * (n - 1 - seat)) / (n - 1));
}


export const activeSeat = (s: State): number => seatForTurn(s.turn, s.n, s.mode.turns);

export function createState(modeId: string, seed: number, n: number): State {
  const mode = modeOf(modeId);
  const players = Math.max(1, Math.min(4, n | 0));
  return {
    mode,
    seed: seed >>> 0,
    n: players,
    log: [],
    turn: 0,
    phase: PH_ACTIVE,
    sheets: Array.from({ length: players }, (_, seat) => {
      const sh = createSheet();
      sh.pts = headStart(mode.id, seat, players);
      return sh;
    }),
    pool: poolFor(seed, 0, mode.pool),
    takenIdx: -1,
    spill: [],
    acted: new Array<boolean>(players).fill(false),
    over: false,
  };
}

export const isOver = (s: State): boolean => s.over;

/** Whose decision is outstanding right now. In PH_SPILL that is several seats at once. */
export function pendingSeats(s: State): number[] {
  if (s.over) return [];
  if (s.phase === PH_ACTIVE) return [activeSeat(s)];
  const a = activeSeat(s);
  const out: number[] = [];
  for (let i = 0; i < s.n; i++) if (i !== a && !s.acted[i]) out.push(i);
  return out;
}

export const isPending = (s: State, seat: number): boolean => pendingSeats(s).includes(seat);

/**
 * Every action `seat` may legally take right now. Deliberately small: at most
 * pool × (placements + shim variants), which is what keeps the bot and the sim cheap.
 */
export function legalActions(s: State, seat: number): number[] {
  if (s.over || !isPending(s, seat)) return [];
  const sheet = s.sheets[seat];
  const dice = s.phase === PH_ACTIVE ? s.pool : s.spill;
  const out: number[] = [];
  const seen = new Set<number>();

  const shims: number[] = [SHIM_NONE];
  if (sheet.shims > 0) shims.push(SHIM_UP, SHIM_DOWN);

  for (let d = 0; d < dice.length; d++) {
    // Identical faces are the same decision; offering both doubles the log's branching for nothing.
    if (seen.has(dice[d])) continue;
    seen.add(dice[d]);
    let anyLegal = false;
    for (const sh of shims) {
      const face = dice[d] + shimDelta(sh);
      if (face < 1 || face > 6) continue;
      for (const [track, slot] of placementsFor(sheet, face)) {
        anyLegal = true;
        out.push(pack(s.turn, s.phase, seat, d, sh, track, slot));
      }
    }
    // A die you took that has no home anywhere is discarded for a shim. Only the ACTIVE player can
    // do this, and only when it is genuinely dead — a voluntary discard would be a free denial move
    // with no cost, which is exactly the dominant line the design panel warned about.
    if (!anyLegal && s.phase === PH_ACTIVE) out.push(pack(s.turn, s.phase, seat, d, SHIM_NONE, T_NONE, 0));
  }

  // YOU MUST TAKE FROM THE SPILL IF YOU CAN. Taking nothing banks a shim, so making it voluntary
  // made declining a paid action — and the sim's bots duly declined all game (see SHIM_POINTS).
  // Restricting it to "nothing here is legal" turns the shim into what it was meant to be: the
  // compensation for being starved, never a move you choose. It also puts real pressure on a late
  // sheet, where the only legal home for a die is one you did not want to spend.
  if (s.phase === PH_SPILL && out.length === 0) out.push(pack(s.turn, s.phase, seat, DIE_NONE, SHIM_NONE, T_NONE, 0));
  return out;
}

function advance(s: State): void {
  // The turn ends when every non-active seat has acted on the spill. A solo state (n === 1) has no
  // spill phase at all.
  if (s.phase === PH_ACTIVE) {
    if (s.n > 1) {
      s.phase = PH_SPILL;
      s.acted = new Array<boolean>(s.n).fill(false);
      return;
    }
  } else {
    const a = activeSeat(s);
    for (let i = 0; i < s.n; i++) if (i !== a && !s.acted[i]) return;
  }
  s.turn++;
  s.phase = PH_ACTIVE;
  s.takenIdx = -1;
  s.spill = [];
  s.acted = new Array<boolean>(s.n).fill(false);
  if (s.turn >= s.mode.turns) {
    s.over = true;
    s.pool = [];
    return;
  }
  s.pool = poolFor(s.seed, s.turn, s.mode.pool);
}

/** Apply one legal action. Returns false and changes nothing if it is not legal right now. */
export function step(s: State, action: number, ev: PlyEvent[] = []): boolean {
  const a = action >>> 0;
  if (s.over) return false;
  const seat = aSeat(a);
  if (aTurn(a) !== s.turn || aPhase(a) !== s.phase) return false;
  if (!legalActions(s, seat).includes(a)) return false;

  const sheet = s.sheets[seat];
  const die = aDie(a);
  const track = aTrack(a);

  if (s.phase === PH_ACTIVE) {
    const face = s.pool[die];
    const { spill } = splitPool(s.pool, die);
    s.takenIdx = die;
    s.spill = spill;
    ev.push({ kind: 'spill', seat, n: spill.length });
    if (track === T_NONE) {
      sheet.passed++;
      ev.push({ kind: 'dead', seat, face });
      grantShim(sheet, seat, ev);
    } else {
      place(sheet, seat, track, face + shimDelta(aShim(a)), aSlot(a), ev);
      if (aShim(a) !== SHIM_NONE) sheet.shims--;
    }
  } else {
    s.acted[seat] = true;
    if (die === DIE_NONE || track === T_NONE) {
      sheet.passed++;
      ev.push({ kind: 'pass', seat });
      grantShim(sheet, seat, ev);
    } else {
      const face = s.spill[die];
      place(sheet, seat, track, face + shimDelta(aShim(a)), aSlot(a), ev);
      if (aShim(a) !== SHIM_NONE) sheet.shims--;
      if (s.mode.tribute) {
        // The dealer's cut. Paid by the house, not out of the taker's score — a passive player who
        // is forced to take (which they usually are) must not be punished for the rule.
        const host = activeSeat(s);
        s.sheets[host].tips += 1;
        ev.push({ kind: 'tip', seat: host, n: 1 });
      }
    }
  }

  s.log.push(a);
  advance(s);
  return true;
}

/**
 * The canonical order of a log. (turn, phase, seat) live in the HIGH bits, so a plain ascending
 * sort IS the order the rules resolve in — which is why two peers that hold the same SET of
 * actions necessarily hold the same state, whatever order the datagrams arrived in.
 */
export function canonical(log: number[]): number[] {
  const bySlot = new Map<number, number>();
  for (const raw of log) {
    const a = raw >>> 0;
    const k = aSlotKey(a);
    const cur = bySlot.get(k);
    // Two peers can both fill in for a seat that went quiet. The fill-in is deterministic so the
    // actions are normally byte-identical; if they are not, the smaller one wins on both peers.
    if (cur === undefined || a < cur) bySlot.set(k, a);
  }
  return [...bySlot.values()].sort((x, y) => x - y);
}

export interface ReplayResult {
  state: State;
  /** The first action that was not legal — a build mismatch, not a desync we can recover from. */
  bad: number | null;
  events: PlyEvent[];
}

/** The whole state as a pure function of (mode, seed, players, log). There is no other path in. */
export function replay(modeId: string, seed: number, n: number, log: number[]): ReplayResult {
  const state = createState(modeId, seed, n);
  const events: PlyEvent[] = [];
  for (const a of canonical(log)) {
    if (state.over) break;
    if (!step(state, a, events)) return { state, bad: a, events };
  }
  return { state, bad: null, events };
}

/** FNV-1a over everything a peer must agree on. Cheap, and it catches a rules drift immediately. */
export function digest(s: State): number {
  let h = 0x811c9dc5;
  const mix = (v: number): void => {
    h ^= v & 0xff;
    h = Math.imul(h, 0x01000193) >>> 0;
    h ^= (v >>> 8) & 0xff;
    h = Math.imul(h, 0x01000193) >>> 0;
  };
  mix(s.turn);
  mix(s.phase);
  mix(s.over ? 1 : 0);
  for (const sheet of s.sheets) {
    for (const v of sheet.ladder) mix(v + 2);
    for (const v of sheet.tally) mix(v);
    mix(sheet.run);
    for (const v of sheet.weights) mix(v);
    mix(sheet.shims);
    mix(sheet.pts);
    mix(sheet.tips);
    mix(sheet.written);
    mix(sheet.passed);
    mix(sheet.chain);
  }
  return h >>> 0;
}

/** Final standings, highest first. Every player, always — never just the local one. */
export interface Standing {
  seat: number;
  breakdown: Breakdown;
  rank: number;
}

export function standings(s: State): Standing[] {
  const rows = s.sheets.map((sheet, seat) => ({ seat, breakdown: score(sheet, s.mode), rank: 0 }));
  rows.sort((a, b) => b.breakdown.total - a.breakdown.total || a.seat - b.seat);
  let rank = 0;
  let last = -1;
  rows.forEach((r, i) => {
    if (r.breakdown.total !== last) {
      rank = i + 1;
      last = r.breakdown.total;
    }
    r.rank = rank;
  });
  return rows;
}

/** The winning seats (more than one on a tie). */
export function winners(s: State): number[] {
  const rows = standings(s);
  if (rows.length === 0) return [];
  const top = rows[0].breakdown.total;
  return rows.filter((r) => r.breakdown.total === top).map((r) => r.seat);
}
